import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { addLine, changeQuantity, removeBox, removeLine, type CartLine } from './cart'

/**
 * The basket, persisted.
 *
 * The rules are in `cart.ts` so they can be tested without a store; this is the thin part around them.
 *
 * **Versioned, which legacy is not.** `fastkitchen-cart` has no version, so when the shape of a line
 * changed the migration was written into the *homepage*: `Home.tsx` deletes any `lunch_menu` line with
 * no `lunchMealType` on mount, commented *"items without it are from old code and must go."* A store
 * that cannot migrate itself makes every page that reads it responsible for every shape it has ever
 * had. `version` and `migrate` put that in one place.
 *
 * **Per-origin, and that is correct.** localStorage does not cross origins and branches are separate
 * origins, so a basket does not follow a branch switch — a different branch is a different menu at
 * different prices, and carrying one across would be worse. It is invisible unless said, so the branch
 * switcher says it.
 */

interface CartState {
  lines: CartLine[]
  add: (line: CartLine) => void
  changeBy: (key: string, delta: number) => void
  remove: (key: string) => void
  removeBoxGroup: (boxGroupId: string) => void
  /** Used after `reconcile` when the catalogue no longer has something. */
  keepOnly: (lines: CartLine[]) => void
  clear: () => void
}

export const useCart = create<CartState>()(
  persist(
    (set) => ({
      lines: [],
      add: (line) => set((state) => ({ lines: addLine(state.lines, line) })),
      changeBy: (key, delta) => set((state) => ({ lines: changeQuantity(state.lines, key, delta) })),
      remove: (key) => set((state) => ({ lines: removeLine(state.lines, key) })),
      removeBoxGroup: (boxGroupId) =>
        set((state) => ({ lines: removeBox(state.lines, boxGroupId) })),
      keepOnly: (lines) => set({ lines }),
      clear: () => set({ lines: [] }),
    }),
    {
      // A new name on purpose. Legacy's `fastkitchen-cart` holds whole product objects with prices in
      // them (FB-C02); adopting the key would mean inheriting those and guessing at their age.
      name: 'agos-cart',
      version: 1,
      partialize: (state) => ({ lines: state.lines }),
      migrate: (persisted, version) => {
        // Nothing to migrate yet. The hook exists so the *next* shape change is a function here rather
        // than a cleanup loop on the homepage.
        if (version === 1) return persisted as { lines: CartLine[] }
        return { lines: [] }
      },
    },
  ),
)
